import { InventoryResponse } from "@shared/types";
import { css } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

/**
* Represents the Canvas page
*/
export class InventoryCanvas extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private list: HTMLUListElement;

    public constructor() {
        super();

        const shadow: ShadowRoot = this.attachShadow({ mode: "open" });

        const container: HTMLDivElement = document.createElement("div");
        container.id = "inventory-container";

        const title: HTMLDivElement = document.createElement("div");
        title.id = "inventory-title";
        title.textContent = "Inventory";
        const containerList: HTMLDivElement = document.createElement("div");
        containerList.id = "inventory-list-container";
        containerList.style.display = "none";

        this.list = document.createElement("ul");
        this.list.id = "inventory-list";

        containerList.appendChild(this.list);

        container.appendChild(title);
        container.appendChild(containerList);
        shadow.appendChild(container);

        container.addEventListener("click", () => {
            containerList.style.display = containerList.style.display === "none" ? "block" : "none";
        });

        const styles: HTMLStyleElement = document.createElement("style");
        styles.textContent = css`
  #inventory-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    z-index: 200;
    position: absolute;
    top: 100px;
    left: 20px;
    width: 220px;
    background: linear-gradient(135deg, #1e1e2f, #2c2c3e);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    font-family: "Segoe UI", sans-serif;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
  }

  #inventory-title {
    padding: 12px;
    text-align: center;
    background-color: #2c3e50;
    font-weight: bold;
    letter-spacing: 1px;
    font-size: 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    user-select: none;
  }

  #inventory-list-container {
    padding: 12px;
    background-color: rgba(255, 255, 255, 0.03);
    display: none;
    max-height: 300px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #888 transparent;
  }

  #inventory-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
    margin: 0;
    list-style-type: none;
  }

  #inventory-list li {
    background: rgba(255, 255, 255, 0.05);
    padding: 8px 12px;
    border-radius: 8px;
    text-align: center;
    font-size: 15px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    transition: background 0.2s;
  }

  #inventory-list li:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

        shadow.appendChild(styles);
        void this.refreshGameState();
        this.observeInventoryChanges();
    }

    private async refreshGameState(): Promise<void> {
        try {
            const response: InventoryResponse | undefined = (await this._gameRouteService.getInventory()) as InventoryResponse | undefined;

            if (!response || !Array.isArray(response.inventory)) {
                this.list.innerHTML = "<li>No items in inventory</li>";
                return;
            }

            this.list.innerHTML = response.inventory.map(item => `<li>${item}</li>`).join("");
        }
        catch (error) {
            console.error("Error fetching inventory:", error);
            this.list.innerHTML = `<li>Error: ${(error as Error).message}</li>`;
        }
    }

    private observeInventoryChanges(): void {
        setInterval(() => this.refreshGameState(), 1000); // 1x per second
    }
}
